<template>
  <div class="table-container">
    <div class="numerai_table">
      <div class="table-header-group">
        <div
          v-for="(col, index) in columns"
          :key="col.name"
          class="table-cell table-header-cell"
          :class="headerCellClasses(col, index)"
          @click="handleColumnClick(index)"
        >
          <template v-if="col.type != 'model'">
            <div class="table_label full-width">
              {{ col.label }}
            </div>
          </template>
          <div v-else class="ml-3">
            <span class="color-primary">Models</span>
            <input
              class="numerai_table_search_box ml-2"
              placeholder="Search"
              :value="searchFilter"
              @input="updateSearchFilter"
            />
          </div>
        </div>
      </div>
      <tbody id="tbody" class="table-row-group"></tbody>
    </div>
    <div
      v-if="formattedRows && formattedRows.length == 0"
      class="text-center no-leaderboard-search-results"
    >
      <div>No matching rows</div>
    </div>
    <div class="text-center py-2">
      <button
        v-if="moreRowsToShow && formattedRows.length !== 0"
        id="load-all-button"
        class="numerai-button button-subtle"
        @click="loadAll"
      >
        Load All ({{ formattedRows.length.toLocaleString() }})
      </button>
    </div>
  </div>
</template>
<script>
/* 
columns:
an array of objects with:
`name` (row val is read from property with this name)
`type` (data type, eg. 'nmr' 'score' 'model')
`label` (what's displayed at the top of the column)

rows:
an array of objects with properties that match `name`s of columns
*/

import { formatCurrency, formatScore } from '@/lib/utils'
import _ from 'lodash'
export default {
  components: {},
  props: {
    columns: { type: Array, default: null },
    rows: { type: Array, default: null },
    sortBy: { type: Number, default: null },
    isMobile: { type: Boolean, default: false },
  },
  data() {
    return {
      rowsLimit: 100,
      loadAllIncrement: 7000, // added to rows limit when 'load all' clicked
      sortingByCol: this.sortBy, // the column index to be sorting by
      sortingReversed: false,
      searchFilter: '',
    }
  },
  computed: {
    moreRowsToShow() {
      return this.formattedRows.length > this.rowsLimit
    },
    formattedRows() {
      if (!this.rows) {
        return null
      }

      let _rows = this.rows

      const nullsAllowed = ['score', 'nmr']
      // put nulls/empties at the end
      const nullsAtEnd = ['return']

      if (this.sortingByCol) {
        const sortingByName = this.columns[this.sortingByCol].name
        const sortingByType = this.columns[this.sortingByCol].type

        _rows = _rows.sort((a, b) => {
          if (nullsAllowed.includes(sortingByType)) {
            if (this.sortingReversed) {
              return a[sortingByName] - b[sortingByName]
            } else {
              return b[sortingByName] - a[sortingByName]
            }
          } else if (nullsAtEnd.includes(this.sortingType)) {
            if (b[sortingByName] === a[sortingByName]) {
              return 0
            } else if (!b[sortingByName]) {
              // empties to the end
              return -1
            } else if (!a[sortingByName]) {
              // empties to the end
              return 1
            }
            if (this.sortingReversed) {
              return a[sortingByName] - b[sortingByName]
            } else {
              return b[sortingByName] - a[sortingByName]
            }
          }
        })
      }

      _rows = _rows.map((row, index) => {
        let rank

        if (!this.sortingReversed) {
          rank = index + 1
        } else {
          rank = _rows.length - index
        }

        return {
          _tableRank: rank,
          ...row,
        }
      })

      if (this.searchFilter.length > 0) {
        _rows = _rows.filter(row => row.model.name.includes(this.searchFilter))
      }

      return _rows
    },
    sortingType() {
      return this.columns[this.sortingByCol].type
    },
  },
  watch: {
    sortingByCol: function(newState, oldState) {
      if (!_.isEqual(newState, oldState)) {
        this.makeTable()
      }
    },
    sortingReversed: function(newState, oldState) {
      if (!_.isEqual(newState, oldState)) {
        this.makeTable()
      }
    },
    formattedRows: function(newState, oldState) {
      if (!_.isEqual(newState, oldState)) {
        this.makeTable()
      }
    },
    rowsLimit: function(newState, oldState) {
      if (!_.isEqual(newState, oldState)) {
        this.makeTable()
      }
    },
  },
  mounted() {
    this.makeTable()
  },
  methods: {
    updateSearchFilter: _.debounce(function(e) {
      this.searchFilter = _.toLower(e.target.value)
    }, 300),
    handleColumnClick(_colIndex) {
      if (this.columns[_colIndex].type == 'model') return

      if (this.sortingByCol == _colIndex) {
        // if already sorting by the clicked column, reverse it
        this.sortingReversed = !this.sortingReversed
      } else {
        this.sortingReversed = false
        this.sortingByCol = _colIndex
      }
    },
    loadAll() {
      // document.getElementById('load-all-button').innerHTML = 'Loading...'
      this.rowsLimit += this.loadAllIncrement
    },
    headerCellClasses(_col, _index = null) {
      let classes = ''

      switch (_col.type) {
        case 'nmr':
          classes += 'nmr '
      }

      if (this.sortingByCol == _index) {
        classes += 'col_selected '

        if (this.sortingReversed) {
          classes += 'col_selected_reversed '
        }
      }

      if (_col.hiddenOnMobile) {
        classes += 'hidden-on-mobile '
      }

      return classes
    },
    makeTable() {
      // Remove existing rows
      const parent = document.getElementById('tbody')
      while (parent.firstChild) {
        parent.firstChild.remove()
      }

      if (!this.formattedRows) {
        return null
      }

      let fragment = document.createDocumentFragment()

      for (
        var i = 0;
        i < this.formattedRows.length && i < this.rowsLimit;
        i++
      ) {
        fragment.append(this.makeRow(this.formattedRows[i]))
      }
      parent.append(fragment)
    },
    makeRow(_rowData) {
      if (!_rowData) return false

      let rowElement = document.createElement('a')
      rowElement.classList.add('table-row')
      rowElement.setAttribute('href', _rowData._link)
      if (_rowData._link) {
        rowElement.onclick = event => {
          if (
            event.ctrlKey ||
            event.shiftKey ||
            event.metaKey || // apple
            (event.button && event.button == 1) // middle click
          ) {
            return
          }
          this.$router.push(_rowData._link)
          event.preventDefault()
        }
      }

      // TODO: Make `number` type
      for (let colIndex = 0; colIndex < this.columns.length; colIndex++) {
        // Rank + Model Cell
        if (this.columns[colIndex].type == 'model') {
          // Make the first cell containing rank and username and badges
          let userInfoCellElement = document.createElement('div')
          if (this.columns[colIndex].hiddenOnMobile) {
            userInfoCellElement.classList.add('hidden-on-mobile')
          }
          userInfoCellElement.classList.add('table-cell')
          userInfoCellElement.classList.add('first-cell')
          // userInfoCellElement.classList.add('d-flex')
          userInfoCellElement.classList.add('cell-model')

          let userInfoElement = document.createElement('div')
          userInfoElement.classList.add('flexrow')
          userInfoElement.classList.add('align_center')
          let rankElement = document.createElement('div')
          rankElement.classList.add('rank')
          // rankElement.innerHTML = _rowData.leaderboardRank
          rankElement.innerHTML = _rowData._tableRank

          userInfoElement.append(rankElement)

          let picContainerElement = document.createElement('div')
          picContainerElement.classList.add('profile-picture-container')
          picContainerElement.classList.add('hidden-on-mobile')

          // Only append the profile picture if not on mobile
          // hidden-on-mobile is no good because it still loads the src
          if (_rowData.model.profileUrl && !this.isMobile) {
            let profilePicElement = document.createElement('img')
            profilePicElement.classList.add('profile-picture')
            profilePicElement.classList.add('hidden-on-mobile')
            profilePicElement.style.backgroundImage =
              "url('" + _rowData.model.profileUrl + "')"
            profilePicElement.setAttribute('loading', 'lazy')
            picContainerElement.append(profilePicElement)
          }

          userInfoElement.append(picContainerElement)

          let usernameAndBadgesElement = document.createElement('div')
          let usernameFlexrowElement = document.createElement('div')
          usernameFlexrowElement.classList.add('flexrow')
          let usernameSpanElement = document.createElement('span')
          usernameSpanElement.innerHTML = _rowData.model.name
          usernameFlexrowElement.append(usernameSpanElement)

          // Add the team symbol
          if (_rowData.model.team && !this.isMobile) {
            let teamIconSpanElement = document.createElement('div')
            teamIconSpanElement.setAttribute(
              'title',
              'Model created by the Numerai team',
            )
            let teamIconElement = document.createElement('img')
            teamIconElement.setAttribute('src', '/img/Numerai-Sigil-Black.png')
            teamIconElement.classList.add('team-badge')
            teamIconElement.classList.add('invert-on-darkmode')
            teamIconElement.classList.add('hidden-on-mobile')
            teamIconSpanElement.append(teamIconElement)
            usernameFlexrowElement.append(teamIconSpanElement)
          }

          // Badges
          let badgeRowElement = document.createElement('div')
          badgeRowElement.classList.add('flexrow')
          badgeRowElement.classList.add('align_center')
          badgeRowElement.classList.add('pt-1')

          // if (
          //   this.mmcBadgeText(_rowData.payoutSelection) &&
          //   _rowData.nmrStaked > 0
          // ) {
          //   let mmcBadge = document.createElement('div')
          //   mmcBadge.setAttribute('title', this.stakingOnMMC)
          //   mmcBadge.classList.add('numerai-badge-small')
          //   mmcBadge.classList.add('mmcBadge')
          //   mmcBadge.classList.add('hidden-on-mobile')
          //   // mmcBadge.innerHTML =
          // this.mmcBadgeText(_rowData.payoutSelection)
          //   badgeRowElement.append(mmcBadge)
          // }

          if (_rowData.model.computeEnabled) {
            let computeBadge = document.createElement('div')
            computeBadge.setAttribute(
              'title',
              'Automatically submitting using Numerai Compute',
            )
            computeBadge.classList.add('numerai-badge-small')
            computeBadge.classList.add('computeBadge')
            computeBadge.classList.add('hidden-on-mobile')
            computeBadge.innerHTML = 'Compute'
            badgeRowElement.append(computeBadge)
          }

          // if (_rowData.model.computeEnabled) {

          // }

          usernameAndBadgesElement.append(usernameFlexrowElement)
          usernameAndBadgesElement.append(badgeRowElement)
          userInfoElement.append(usernameAndBadgesElement)
          userInfoCellElement.append(userInfoElement)
          rowElement.append(userInfoCellElement)
        }

        // NMR Cell
        else if (this.columns[colIndex].type == 'nmr') {
          let stakeCellElement = document.createElement('div')
          stakeCellElement.classList.add('data')
          if (this.columns[colIndex].hiddenOnMobile) {
            stakeCellElement.classList.add('hidden-on-mobile')
          }
          // stakeCellElement.classList.add('stake')
          stakeCellElement.classList.add('table-cell')
          if (parseFloat(_rowData[this.columns[colIndex].name])) {
            stakeCellElement.classList.add('color-nmr')

            if (parseFloat(_rowData[this.columns[colIndex].name]) < 0) {
              stakeCellElement.classList.add('color-negative')
            }
            // stakeCellElement.setAttribute(
            //   'title',
            //   '$' + _rowData.nmrStakedToUsd,
            // )
            stakeCellElement.innerHTML =
              formatCurrency(_rowData[this.columns[colIndex].name]) + ' NMR'
          } else {
            stakeCellElement.classList.add('null-number')
            stakeCellElement.setAttribute('title', 'No NMR')
          }

          rowElement.append(stakeCellElement)
        }

        // Score cell
        // HTML: <td><a class="data">{rep}</a></td>
        else if (this.columns[colIndex].type == 'score') {
          let scoreCellElement = document.createElement('div')
          if (this.columns[colIndex].hiddenOnMobile) {
            scoreCellElement.classList.add('hidden-on-mobile')
          }
          scoreCellElement.classList.add('table-cell')
          scoreCellElement.classList.add('data')
          scoreCellElement.innerHTML = formatScore(
            _rowData[this.columns[colIndex].name],
          )
          rowElement.append(scoreCellElement)
        }
      }
      return rowElement
    },
  },
}

/*

*/
</script>
<!-- TODO: Unscoped. Should make more specific class names/selectors -->
<style>
.numerai_table {
  width: 100%;
  border-collapse: collapse;
  display: table;
  font-family: var(--ui-font);
}

.table-header-group {
  display: table-header-group;
  position: sticky;
  height: 48px;
  font-size: 0.8em;
  font-weight: 500;
  text-transform: uppercase;
  font-family: var(--brand-font);
  width: 10%;
  text-align: right;
  background-color: var(--paper-background-color);
  color: var(--color-tertiary);
}

.table-header-cell {
  display: table-cell;
  cursor: pointer;
}

.table-header-cell:first-child {
  text-align: left;
}

.table-header-cell:hover {
  color: var(--color-primary);
}

.table-row {
  display: table-row;
  border-top: 1px solid var(--color-very-subtle);
}

a.table-row {
  text-decoration: none;
}

.table-row:hover {
  background-color: var(--paper-background-highlight1) !important;
}

.cell-rank {
  color: var(--color-secondary);
  font-weight: bold;
  padding-left: 0.6em;
  padding-left: 0.6em;
  width: 3.6em;
  max-width: 3.6em;
  min-width: 3.6em;
  display: flex;
  align-items: center;
  font-family: var(--ui-font);
}

.cell-model {
  padding-left: 0.4em;
  min-width: 200px;
  text-transform: uppercase;
  color: var(--color-secondary);
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  text-transform: uppercase;
  font-family: var(--brand-font);
  display: flex;
  align-items: center;
  font-size: 0.9em;
  width: 25%;
}

.table-cell {
  padding: 0.8em 0.8em;
  vertical-align: middle;
  outline: none;
  border: 0;
  display: table-cell;
  white-space: nowrap;
}

.team-badge {
  display: flex;
  margin-left: 2px;
  margin-bottom: -2px;
  width: 15px;
}

.rank {
  color: var(--color-tertiary);
  width: 3.6em;
  max-width: 3.6em;
  min-width: 3.6em;
  display: flex;
  align-items: center;
  font-family: var(--ui-font);
}

.data {
  overflow: hidden;
  text-align: right;
}

.table-header-cell.col_selected {
  color: var(--color-primary);
  white-space: nowrap;
  text-shadow: 0 0 5px #fff5;
}

.table-header-cell.col_selected .table_label:before {
  font-size: 0.8em;
  content: '▼ ';
  margin-right: 0.4em;
  margin-bottom: 10px;
  padding-bottom: 2px;
  white-space: nowrap;
}

.table-header-cell.col_selected.col_selected_reversed .table_label:before {
  content: '▲ ';
}

.numerai_table_search_box {
  font-size: 0.9em;
  text-transform: uppercase;
}

.numerai_table input:focus {
  outline: none;
}

.table-search-box::placeholder {
  opacity: 0.4;
}

.profile-picture-container {
  width: 3em;
  height: 3em;
  min-width: 3em;
  min-height: 3em;
  max-width: 3em;
  max-height: 3em;
  margin-bottom: 0px;
  margin-right: 10px;
  border-radius: 1.5em;
  border: 2px solid var(--paper-border-color);
  background-color: var(--paper-background-color);
  overflow: hidden;
}

.profile-picture {
  width: 3em;
  height: 3em;
  min-width: 3em;
  min-height: 3em;
  max-width: 3em;
  max-height: 3em;
  margin-left: -2px;
  margin-top: -2px; /* undoing the container border */
  background-color: var(--color-very-subtle);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

@media screen and (max-width: 1390px) {
  .table-container {
    overflow-x: scroll;
  }
}
</style>
